import { ArrowLeft, Info, Plus, RefreshCw } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGetStoreQuery } from '@/store/api/directoryApi';
import {
  useCreateSimpleRoleMutation,
  useGetSimpleRoleQuery,
  useGetSimpleRolesQuery,
} from '@/store/api/simpleRolesApi';

export function SimpleRolesPage() {
  const { storeId, roleId } = useParams<{ storeId: string; roleId?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: store, isLoading: isLoadingStore } = useGetStoreQuery(storeId ?? '', {
    skip: !storeId,
  });
  const {
    data: roles,
    isLoading: isLoadingRoles,
    isError,
    refetch,
  } = useGetSimpleRolesQuery(
    { orgId: store?.orgId ?? '' },
    { skip: !store?.orgId || Boolean(roleId) },
  );
  const {
    data: role,
    isLoading: isLoadingRole,
    isError: isRoleError,
    refetch: refetchRole,
  } = useGetSimpleRoleQuery(roleId ?? '', { skip: !roleId });
  const [createRole, { isLoading: isCreating }] = useCreateSimpleRoleMutation();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [capabilities, setCapabilities] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!store?.orgId || !name.trim()) {
      return;
    }
    const actions = [
      ...new Set(
        capabilities
          .split(/[\n,]/)
          .map((value) => value.trim())
          .filter(Boolean),
      ),
    ];
    if (!actions.length) {
      return;
    }
    try {
      const created = await createRole({
        orgId: store.orgId,
        name: name.trim(),
        capabilities: actions,
      }).unwrap();
      toast.success(`Role “${name.trim()}” created`);
      setCreateOpen(false);
      setName('');
      setCapabilities('');
      navigate(`/stores/${store.id}/roles/${created.roleId}`);
    } catch {
      toast.error(
        'Could not create the role. Check the name and capability actions, then try again.',
      );
    }
  }

  if (isLoadingStore) {
    return <LoadingSpinner className="py-12" />;
  }
  if (!store) {
    return (
      <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
        Store scope is unavailable. Return to{' '}
        <Link className="text-primary underline" to="/stores">
          Stores &amp; workspaces
        </Link>
        .
      </div>
    );
  }

  if (roleId) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/stores/${store.id}/roles`)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Simple roles · {store.name}
        </Button>
        {isLoadingRole ? (
          <LoadingSpinner className="py-10" />
        ) : isRoleError || !role ? (
          <Retry
            onRetry={() => {
              void refetchRole();
            }}
          />
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold">{role.name}</h1>
              <p className="text-sm text-muted-foreground">
                Organization-scoped role · store context: {store.name}
              </p>
            </div>
            <Card>
              <CardContent className="space-y-5 pt-6">
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Detail label="Role ID" value={role._id} mono />
                  <Detail label="Status" value={role.isActive ? 'active' : 'inactive'} />
                  <Detail label="Organization" value={role.orgId ?? store.orgId} mono />
                  <Detail
                    label="Updated"
                    value={role.updatedAt ? new Date(role.updatedAt).toLocaleString() : '—'}
                  />
                </dl>
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold">Capabilities</h2>
                    <span
                      title="The backend has no capability-aware role update contract. Existing capabilities are read-only; create a new simple role to use a different set."
                      aria-label="Capability update limitation"
                      tabIndex={0}
                    >
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Assignments shown here are derived from the role’s returned policy specs; no
                    update action is available.
                  </p>
                  <ul className="mt-3 space-y-2">
                    {role.policySpecs
                      .flatMap((spec) =>
                        spec.statements.flatMap((statement) =>
                          (statement.actions ?? []).map((action) => ({ action, spec: spec.name })),
                        ),
                      )
                      .map(({ action, spec }, index) => (
                        <li
                          key={`${spec}-${action}-${index}`}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
                        >
                          <span>{humanizeAction(action)}</span>
                          <code className="text-xs text-muted-foreground">{action}</code>
                        </li>
                      ))}
                  </ul>
                  {role.policySpecs.length === 0 && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      No capability actions were returned for this role.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  const roleList = roles ?? [];
  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate(`/stores/${store.id}`)}>
        <ArrowLeft className="mr-2 h-4 w-4" /> {store.name}
      </Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Simple roles</h1>
          <p className="text-sm text-muted-foreground">
            Organization scope: <span className="font-mono">{store.orgId}</span>. Creation uses the
            capability compiler.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Create role
        </Button>
      </div>
      <div
        role="note"
        className="flex gap-2 rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Capabilities for an existing simple role are read-only. The service does not provide
          capability-aware updates; create a new role for a different capability set.
        </p>
      </div>
      {isLoadingRoles ? (
        <LoadingRows label="Loading simple roles" columns={4} />
      ) : isError ? (
        <Retry
          onRetry={() => {
            void refetch();
          }}
        />
      ) : roleList.length === 0 ? (
        <EmptyState
          title="No simple roles yet"
          description="Create a role using the organization’s supported capability compiler."
          action={<Button onClick={() => setCreateOpen(true)}>Create role</Button>}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-muted/40">
                  <tr>
                    {['Name', 'Scope', 'Status', 'Updated'].map((label) => (
                      <th key={label} className="px-4 py-3 font-medium">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roleList.map((item) => (
                    <tr key={item._id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium">
                        <Link
                          className="text-primary hover:underline"
                          to={`/stores/${store.id}/roles/${item._id}`}
                        >
                          {item.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">Organization · {item.orgId ?? store.orgId}</td>
                      <td className="px-4 py-3">{item.isActive ? 'active' : 'inactive'}</td>
                      <td className="px-4 py-3">
                        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create simple role</DialogTitle>
            <DialogDescription>
              The compiler validates capability identifiers against its current dictionary. Existing
              roles cannot be edited here.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="simple-role-name">Role name</Label>
              <Input
                id="simple-role-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="simple-role-capabilities">Capability actions</Label>
              <textarea
                id="simple-role-capabilities"
                required
                rows={6}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder={'customers:read\ncustomers:update'}
                value={capabilities}
                onChange={(e) => setCapabilities(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Enter one supported capability action per line or separate them with commas. The
                server remains authoritative.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? 'Creating…' : 'Create role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function humanizeAction(action: string): string {
  return action
    .split(':')
    .map((part) => part.replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()))
    .join(' · ');
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm ${mono ? 'break-all font-mono' : ''}`}>{value || '—'}</dd>
    </div>
  );
}

function Retry({ onRetry }: { onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
      Could not load this role.{' '}
      <Button variant="link" onClick={onRetry}>
        <RefreshCw className="mr-1 h-3 w-3" /> Try again
      </Button>
    </div>
  );
}
